/**
 * MONEDA-SERVICE.JS — Admin panel currency service (ESM)
 *
 * Importa la lógica pura de shared/services/moneda/ (SSOT)
 * y agrega estado exclusivo del admin:
 * - Contexto de moneda activa (setContexto/getContexto) para el nodo actual
 * - Tasas de cambio (setTasas/getTasa/convertir) para consolidación
 */
import {
    PRESETS_MONEDA,
    ORDEN_MONEDAS_PARA_SELECTOR,
    MONEDA_POR_DEFECTO,
    obtenerPresetPorCodigo,
    obtenerTodosLosPresetsEnOrden,
    formatearNumeroConSeparadores,
    formatearCentavosConSimbolo,
    formatearCentavosSoloValor,
    unidadesMenoresPorUnidad,
    convertirUnidadesAUnidadMenor,
    calcularTasaCruzada as _calcularTasaCruzadaPura,
    calcularTasaCruzadaONull as _calcularTasaCruzadaONullPura,
    convertirCentavosEntreMonedas as _convertirPura,
    formatearConversionAproximada as _formatearConversionPura,
} from '@shared/services/moneda/moneda-service.js';

// ============================================================
// ESTADO DEL ADMIN (exclusivo de este módulo)
// ============================================================

// Moneda activa del nodo que se está visualizando
let _codigoMonedaContextoActivo = MONEDA_POR_DEFECTO;

// Tasas de cambio cargadas desde el backend (relativas a USD)
let _tasasBaseUsd = null;

// Moneda de VISUALIZACIÓN global del panel (patrón idéntico al idioma:
// localStorage + los paneles conversion-aware la leen al render). null = nativa.
const CLAVE_STORAGE_MONEDA_VISTA = 'adminMonedaVista';
let _monedaVistaGlobal = (() => {
    const guardada = localStorage.getItem(CLAVE_STORAGE_MONEDA_VISTA);
    return guardada && PRESETS_MONEDA[guardada] ? guardada : null;
})();

function getMonedaVista() {
    return _monedaVistaGlobal;
}

/** Setea la moneda de vista global (null/'' = nativa) y la persiste. NO recarga:
 *  el caller decide (header → reload total como el idioma; panel → repaint local). */
function setMonedaVista(codigo) {
    _monedaVistaGlobal = codigo && PRESETS_MONEDA[codigo] ? codigo : null;
    if (_monedaVistaGlobal) localStorage.setItem(CLAVE_STORAGE_MONEDA_VISTA, _monedaVistaGlobal);
    else localStorage.removeItem(CLAVE_STORAGE_MONEDA_VISTA);
}

// ============================================================
// FORMATEO CON CONTEXTO (usa moneda activa como default)
// ============================================================

function format(centavos, moneda) {
    return formatearCentavosConSimbolo(centavos, moneda || _codigoMonedaContextoActivo);
}

/**
 * Convierte un monto en unidades display (lo que tipea el admin: "150.50")
 * a unidad menor de la moneda indicada (BRL/PEN → centavos ×100; PYG/COP → entero ×1).
 * SSOT de entrada de montos: NUNCA hacer Math.round(x * 100) a mano — corrompe
 * monedas de 0 decimales por el factor 100.
 * @param {number|string} unidades - Monto en unidades display
 * @param {string} [moneda] - Código ISO del nodo target; default contexto activo
 * @returns {number} Monto entero en unidad menor
 */
function aUnidadMenor(unidades, moneda) {
    return convertirUnidadesAUnidadMenor(unidades, moneda || _codigoMonedaContextoActivo);
}

function formatValue(centavos, moneda) {
    return formatearCentavosSoloValor(centavos, moneda || _codigoMonedaContextoActivo);
}

// ============================================================
// CONVERSIÓN (usa tasas cargadas en memoria)
// ============================================================

function convertir(centavos, codigoOrigen, codigoDestino) {
    if (!_tasasBaseUsd || codigoOrigen === codigoDestino) return centavos;
    return _convertirPura(centavos, codigoOrigen, codigoDestino, _tasasBaseUsd);
}

function formatConvertido(centavos, codigoOrigen, codigoDestino) {
    if (!_tasasBaseUsd) return format(centavos, codigoOrigen);
    return _formatearConversionPura(centavos, codigoOrigen, codigoDestino, _tasasBaseUsd);
}

function getTasa(codigoOrigen, codigoDestino) {
    if (!_tasasBaseUsd) return 1;
    return _calcularTasaCruzadaPura(codigoOrigen, codigoDestino, _tasasBaseUsd);
}

/**
 * Tasa cruzada HONESTA: null cuando no se conoce (tasas sin cargar —el fetch del
 * arranque es silencioso— o falta la moneda en el mapa). Usar esta, no getTasa,
 * en todo lugar que MUESTRE un monto convertido: getTasa devuelve 1 ante la duda
 * y un "1" entre monedas distintas es un número falso (BRL→PYG real es ~1417).
 * @returns {number|null}
 */
function getTasaONull(codigoOrigen, codigoDestino) {
    return _calcularTasaCruzadaONullPura(codigoOrigen, codigoDestino, _tasasBaseUsd);
}

// ============================================================
// CONTEXTO Y TASAS (estado del admin)
// ============================================================

function setContexto(codigoMoneda) {
    if (PRESETS_MONEDA[codigoMoneda]) {
        _codigoMonedaContextoActivo = codigoMoneda;
    }
}

function getContexto() {
    return _codigoMonedaContextoActivo;
}

function getSimboloActual() {
    return obtenerPresetPorCodigo(_codigoMonedaContextoActivo).simbolo;
}

/** Símbolo de una moneda específica sin tocar el contexto activo.
 *  Útil cuando renderizás info de un nodo cuya moneda difiere de la del admin. */
function getSimboloDe(codigoIso) {
    return obtenerPresetPorCodigo(codigoIso || _codigoMonedaContextoActivo).simbolo;
}

function setTasas(tasas) {
    _tasasBaseUsd = tasas;
}

function tieneTasasCargadas() {
    return _tasasBaseUsd !== null;
}

// ============================================================
// API PÚBLICA
// ============================================================

export const MonedaService = {
    // Presets
    PRESETS: PRESETS_MONEDA,
    ORDER: ORDEN_MONEDAS_PARA_SELECTOR,
    DEFAULT: MONEDA_POR_DEFECTO,

    // Formateo
    format,
    formatValue,
    formatNumber: formatearNumeroConSeparadores,

    // Entrada de montos (unidades display → unidad menor, factor real por moneda)
    aUnidadMenor,

    // Contexto
    setContexto,
    getContexto,
    getSimboloActual,
    getSimboloDe,

    // Acceso a presets
    getPreset: obtenerPresetPorCodigo,
    // Factor unidad↔unidad-menor (SSOT): BRL → 100, PYG → 1
    getUnidadesMenoresPorUnidad: unidadesMenoresPorUnidad,
    getAllPresets: obtenerTodosLosPresetsEnOrden,

    // Moneda de visualización global (persistente, como el idioma)
    getMonedaVista,
    setMonedaVista,

    // Conversión
    convertir,
    formatConvertido,
    getTasa,
    getTasaONull,
    setTasas,
    tieneTasasCargadas,

    /**
     * Genera HTML <option> tags para un <select> de monedas.
     * @param {Object} [opciones]
     * @param {boolean} [opciones.incluirHeredar=false] - Agrega opción "Heredar" con value=""
     * @returns {string} HTML con todas las opciones en orden SSOT
     */
    buildSelectorOptions(opciones) {
        var incluirHeredar = opciones && opciones.incluirHeredar;
        var presets = obtenerTodosLosPresetsEnOrden();
        var html = incluirHeredar ? '<option value="">Heredar</option>\n' : '';
        for (var i = 0; i < presets.length; i++) {
            var p = presets[i];
            html += '<option value="' + p.codigo + '">' + p.codigo + ' — ' + p.nombre + ' (' + p.simbolo + ')</option>\n';
        }
        return html;
    },
};
